import { Color, Spacing } from '@persona-id/design-tokens';
import { GraphIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { Mode } from 'dashboard/components/GraphCanvasV2/behaviors';
import GraphCanvasContextProvider from 'dashboard/components/GraphModule/GraphCanvasContextProvider';
import GraphConfigurationProvider from 'dashboard/components/GraphModule/GraphConfigurationProvider';
import GraphDataWrapper from 'dashboard/components/GraphModule/GraphDataWrapper';
import GraphQueryingProvider from 'dashboard/components/GraphModule/GraphQueryingProvider';
import { useBackend } from 'dashboard/components/GraphModule/hooks/useBackend';
import { useG6Wrapper } from 'dashboard/components/GraphModule/hooks/useG6Wrapper';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
import { useGraphQuerying } from 'dashboard/components/GraphModule/hooks/useGraphQuerying';
import GraphV2Card from 'dashboard/components/GraphV2Card';
import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import { Environment } from 'dashboard/models/environment';
import { Graph, HashableNodeId, NodeId, nodeIdToHashable } from 'dashboard/models/graph';
import {
  GraphExplorerQueryLoadingStage,
  GraphExplorerQueryLoadingState,
  GraphViewTypes,
} from 'dashboard/models/graph-explorer';
import {
  convertToLightlyHydratedNode,
  LightlyHydratedNode,
} from 'dashboard/models/graph-hydration';
import {
  generateVariableMap,
  GraphQuery,
  graphQueryResponseToV2Data,
  GraphQueryStatus,
  ValueType,
} from 'dashboard/models/graph-query';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { User } from 'dashboard/models/user';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

const StyledButton = styled(Button)`
  border: 1px solid ${Color.CoreSlate100};
`;

const NavigationHeaderContainer = styled.div`
  margin-top: ${Spacing[1]};
`;

interface GraphAccountCardProps {
  account: Account;
  accountStatuses: AccountStatus[];
  accountTypes: AccountType[];
  currentEnvironment: Environment;
  currentUser: User;
  graph: Graph;
  graphQueryTemplateId: string;
  graphQueryTemplates: GraphQueryTemplate[];
}

const GraphAccountCard = ({
  account,
  accountTypes,
  graph,
  graphQueryTemplateId,
}: GraphAccountCardProps) => {
  const backend = useBackend();
  const { graphExplorer } = useGraphExplorer();
  const g6Wrapper = useG6Wrapper();

  const { currentGraphQuery, setCurrentGraphQuery, querySentCounterRef } = useGraphQuerying();

  // TODO: Try to unify graph querying callbacks / behavior with what is in GraphQueryingProvider

  // As we do not use the progressbar for this, we can be a bit lazy with loading state.
  // We just use SendingQuery and Idle
  const [queryLoadingState, unwrappedSetQueryLoadingState] =
    useState<GraphExplorerQueryLoadingState>({
      stage: GraphExplorerQueryLoadingStage.Idle,
      setTime: new Date(),
    });
  // adds the time in which the stage is changed
  const updateQueryLoadingState = (stage: GraphExplorerQueryLoadingStage) => {
    unwrappedSetQueryLoadingState({ stage, setTime: new Date() });
  };
  const [errorMessage, unwrappedSetErrorMessage] = useState<string | null>(null);
  const setQueryErrored = (errMessage: string) => {
    updateQueryLoadingState(GraphExplorerQueryLoadingStage.Idle);
    unwrappedSetErrorMessage(errMessage);
  };

  const lightlyHydrateQueryResults = useCallback(
    async (nodeIds: NodeId[]): Promise<Map<HashableNodeId, LightlyHydratedNode>> => {
      return await backend.lightlyHydrate({ nodeIds, graphId: graph.id }).then((response) => {
        const lightHydrationData: Map<HashableNodeId, LightlyHydratedNode> = new Map(
          nodeIds.map((nodeId, index) => {
            const lightlyHydratedNode = convertToLightlyHydratedNode(
              graphExplorer.getDatumTypeByToken(nodeId.token),
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              response['lightly-hydrated-nodes'][index],
            );
            return [nodeIdToHashable(nodeId), lightlyHydratedNode];
          }),
        );
        return lightHydrationData;
      });
    },
    [graph?.id, graphExplorer],
  );

  const handleCompletedGraphQueryResponse = async (
    currentQuerySent: number,
    graphQuery: GraphQuery,
  ) => {
    const response = graphQuery.attributes.result;
    const v2Data = graphQueryResponseToV2Data(response);
    const { nodes } = v2Data;

    // end early if no results found in the query
    if (nodes.length === 0) {
      const message = graphExplorer.setData(v2Data, new Map());
      g6Wrapper.setData(message);
    } else {
      const nodeIds: NodeId[] = nodes.map((node) => node.id);

      // Kick off a call to do light hydration, and render after all of the light hydration data is fetched.
      const lightHydrationData = await lightlyHydrateQueryResults(nodeIds);
      const message = graphExplorer.setData(v2Data, lightHydrationData);
      // Set the G6 state.
      g6Wrapper.setData(message);
    }

    g6Wrapper.setHandleLayoutComplete(() => {
      updateQueryLoadingState(GraphExplorerQueryLoadingStage.Idle);
      g6Wrapper.setHandleLayoutComplete(() => undefined);
    });

    // finished query
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (querySentCounterRef.current !== currentQuerySent) {
      return;
    }

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setCurrentGraphQuery(graphQuery);
  };

  const runGraphQuery = async () => {
    // copy the params
    const parameterMap = generateVariableMap([
      {
        name: 'account_id',
        type: ValueType.Text,
        value: account.id,
      },
    ]);

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    querySentCounterRef.current++;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const currentQuerySent = querySentCounterRef.current;
    updateQueryLoadingState(GraphExplorerQueryLoadingStage.SendingQuery);

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const shouldContinue = () => querySentCounterRef.current === currentQuerySent;
    const onNetworkError = () =>
      setQueryErrored('We are experiencing network issues. Please reload to try again.');
    const onQueryError = () => setQueryErrored('Query has errored. Please reload to try again.');

    const newGraphQuery = await backend.queryWithTemplateAndPoll({
      graphQueryTemplateId,
      parameterMap,
      graphId: graph.id,
      creatorId: account.id,
      creatorType: 'Account',
      shouldContinue,
      onNetworkError,
      onQueryError,
      onQueryPolled: () => undefined,
    });

    if (newGraphQuery && newGraphQuery.attributes.status === GraphQueryStatus.Completed) {
      await handleCompletedGraphQueryResponse(currentQuerySent, newGraphQuery);
    }
  };
  // stop polling on unmount
  useEffect(() => {
    return () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      querySentCounterRef.current++;
    };
  }, []);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [accountQueryRanOn, setAccountQueryRanOn] = useState<string>(null);
  useEffect(() => {
    // break early if unable to perform query
    if (!account || !graphQueryTemplateId || !graph) {
      return;
    }
    // it is possible for an account to change. (Ex: switching to another account page via omnisearch)
    // run another graph query if the account changes.
    if (accountQueryRanOn !== account.id) {
      setAccountQueryRanOn(account.id);
      runGraphQuery();
    }
  }, [account, graphQueryTemplateId, accountQueryRanOn, graph]);

  const isLoadingGraphQuery =
    queryLoadingState.stage !== GraphExplorerQueryLoadingStage.Idle || graphQueryTemplateId == null;
  const loadingGraphEnvironment = graph == null || isLoadingGraphQuery;

  const generateCanvasButtons = () => {
    const toGraphExplorerLink = currentGraphQuery
      ? `/dashboard/explorer?queryId=${currentGraphQuery.id}`
      : null;
    return (
      <Tooltip
        iconTrigger={
          <Link to={toGraphExplorerLink || ''}>
            <StyledButton
              type='button'
              width='icon'
              size='sm'
              variant='inverse'
              disabled={toGraphExplorerLink == null}
            >
              <GraphIcon />
            </StyledButton>
          </Link>
        }
        portal={true}
        side='top'
      >
        <Paragraph color='primary'>Open in Graph Explorer</Paragraph>
      </Tooltip>
    );
  };

  const generateNavigationHeader = () => {
    if (!currentGraphQuery) {
      return null;
    }
    return (
      <NavigationHeaderContainer>
        <Paragraph color='secondary'>
          Last run at{' '}
          {DateTime.fromISO(currentGraphQuery.attributes['created-at']).toFormat(
            DATE_AND_TIME_TZ_FORMAT,
          )}
        </Paragraph>
      </NavigationHeaderContainer>
    );
  };

  return (
    <GraphV2Card
      accountTypes={accountTypes}
      nodeFunctionsConfigs={graph?.attributes['node-functions-configs'] || []}
      canvasButtons={generateCanvasButtons()}
      enableScroll={true}
      navigationHeader={generateNavigationHeader()}
      isLoading={loadingGraphEnvironment}
      overlayMessage={errorMessage}
    />
  );
};

const GraphAccountCardWithContext = (props: GraphAccountCardProps) => {
  const {
    accountStatuses,
    accountTypes,
    currentEnvironment,
    currentUser,
    graph,
    graphQueryTemplates,
  } = props;
  const viewTypeSwitchEnabled = useFeatureFlag('dashboard:graph-pairwise-similarity-nodes');

  return (
    <GraphDataWrapper
      accountStatuses={accountStatuses}
      accountTypes={accountTypes}
      currentEnvironment={currentEnvironment}
      currentUser={currentUser}
      graph={graph}
      graphQueryTemplates={graphQueryTemplates}
    >
      <GraphCanvasContextProvider
        graph={graph}
        enabledModes={[Mode.PAN]}
        viewTypeSwitchEnabled={viewTypeSwitchEnabled}
        viewType={GraphViewTypes.Parsed}
      >
        <GraphConfigurationProvider>
          <GraphQueryingProvider showMessage={() => null}>
            <GraphAccountCard {...props} />
          </GraphQueryingProvider>
        </GraphConfigurationProvider>
      </GraphCanvasContextProvider>
    </GraphDataWrapper>
  );
};

export default GraphAccountCardWithContext;
